/**
 * A held row's facade, `SheetRow.Handle` (published as a member of the
 * `SheetRow` namespace, which `sheet-row.ts` declares): ONE object per row of
 * a sheet, or a standalone row a client holds. It reads its backing
 * `SheetRow` and writes through one writer (`RowWriter`): the client's
 * row-field ledger for the row's own fields, the sheet's cell writer (or the
 * client's own cell cache) for its cells, the move ledger for a timepoint
 * cell's move.
 *
 * Beside the class: the SDK-internal doors onto a facade (`sheetRowOf`,
 * `rowHandleOf`, `leasedRowOf`, `placeInTree`, …, re-exported by
 * `sheet-row.ts`) and the cell-address helpers its verbs share. The facade
 * names the backing row's module only as types; what it needs of it at run
 * time is handed to its constructor (`RowFacadePort`).
 */
import { ListenerErrors, type ListenerErrorSink } from "@tento-core/listener-errors";
import type { Option } from "@tento-lona/sheets";
import { NaiveDate, DateTime, Utc, type DateUnit } from "@tento-chrono";
import { cellReadFailure, ClientDisposedError, type ClientLifecycle } from "../client/handle-errors.ts";
import { nonnull } from "@tento-core/assert";
import { EventOps } from "../indices/event-ops.ts";
import { AliasRow, type CellCardinality, type RowSection, SourceRow } from "@tento-lona/sheets/platform";
import { EVENTS_FACADE } from "@tento-lona/sheets/platform";
import { TypedApi } from "@tento-core/typed-api";
import type { Rational } from "@tento-core/rational";
import { RowLocalId, UserId } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import type { LayoutSpec, Timescale } from "@tento-lona/sheets";
import type { Capabilities, CellLayoutOptions, HeaderSecondRowKind } from "@tento-lona/sheets";
import { RowKey } from "@tento-lona/sheets";
import { RowEnvelopes } from "@tento-lona/sheets";
import {
  aliasWriterOf,
  MoveWrites,
  type RowFacade,
  RowWriter,
  RowWrites,
  sourceWriterOf,
} from "@tento-lona/sheets/internal-sdk";
import { Internals } from "@tento-lona/sheets/internal-ui";
import { CellHandle } from "../cells/cell-handle.ts";
import { RowRangeCellHandle } from "../cells/range-cell-handle.ts";
import { CellEntry } from "@tento-lona/sheets";
import type { CellValue } from "@tento-lona/sheets";
import { CellTarget } from "../cells/cell-target.ts";
import {
  Actor,
  type InvalidationSink,
  RowDataChanged,
} from "../invalidation.ts";
import { cellAccessKey } from "../sync/cell-access-key.ts";
import { CellAccessor } from "../cells/sheet-cell-accessor.ts";
import { afterFailedWrite, AfterFailedWrite } from "../client/after-failed-write.ts";
import { CellWriteError, RowWriteError, SdkRequestError } from "../errors.ts";
import { Durable, DurableHandoffs } from "../client/durable-handoffs.ts";
import { observedDurable } from "../client/unobserved-settled.ts";
import { bothOwners, columnOf, findRowsSideAliasDescendant, turnedMulti } from "./row-facade-support.ts";
import { RowCellCache } from "../sync/row-cell-cache.ts";
import { RowId } from "@tento-lona/sheets";
import { RowPatch } from "@tento-lona/sheets";
import { RowNetworkKey } from "@tento-lona/sheets";
import type { FacadeKey } from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import { type ModelRow, type ModelRowReads, RowKind } from "@tento-lona/sheets";
import { Json, type JsonObject } from "@tento-lona/sheets";
import type { ChildSpec, Graph } from "@tento-lona/sheets";
import { SheetId } from "@tento-lona/sheets";
import {
  RowEvents,
  RowHandleEventsReader,
  EMPTY_ROW_EVENTS_READER,
  type RowEventsReader,
} from "./row-events.ts";
import { RowTypeDescriptor, AliasResolver } from "./row-descriptor.ts";
import type { RowFieldWrites } from "./row-field-writes.ts";
import { PendingRowPersist } from "./pending-row-persist.ts";
import { leaseFace, leaseView, leaseViewTarget } from "./row-lease-view.ts";
import type { JoinedCellWriter, RowMirror, SheetRow, SheetRowContext, SheetRowHandleDeps } from "./sheet-row.ts";
import {
  CellWindow,
  datedCell,
  durableAddress,
  existingAddress,
  heldTimepoint,
  onRow,
  relocateStep,
  takeBackMove,
  timepointOf,
} from "./sheet-row-cells.ts";
import { RowTimepoints } from "./sheet-row-timepoints.ts";
import { type FormulaRewrite, RowUpdates } from "./sheet-row-updates.ts";

/**
 * **SDK-internal** (not on any barrel): the key of a held row's
 * `RowCellCache` accessor. Symbol-keyed so an application — which imports only
 * the barrels — cannot reach the per-row cell cache, while the SDK's clients
 * reach it through `rowHandleOf` and a white-box test's stand-in row
 * implements it (`{ [ROW_HANDLE]: () => rh }`).
 */
export const ROW_HANDLE: unique symbol = Symbol("SheetRow.Handle.rowHandle");

/** What `rowHandleOf` reads: a held row, or a test's stand-in for one. */
export type HasRowHandle = { [ROW_HANDLE](): RowCellCache };

/**
 * **SDK-internal** (not on any barrel): a held row's shared `RowCellCache`, the
 * per-row cell cache, created lazily on first use (firing the row's
 * `onRowHandleInit`). The SDK's clients (events, tasks) and white-box tests
 * reach it here; an application reads a row's cells with `row.cells(range)`
 * and a sheet's with `handle.cells`.
 */
export function rowHandleOf(row: HasRowHandle): RowCellCache {
  return row[ROW_HANDLE]();
}

/**
 * Rewrite `row`'s formula to `formula` for a rename (`RowMirror.Renamed`):
 * painted as `actor` and entered in the row-field ledger now, sent or taken
 * back through the answer.
 *
 * @internal SDK-private (the client's rename propagation); `lib.ts` does not export it.
 */
export function beginFormulaRewrite(row: SheetRow.Handle, formula: string, actor: Actor): FormulaRewrite {
  return facadeWrites.of(Handle).rewrite(row, formula, actor);
}

/**
 * The facade's friend operations, by consumer group. Each group is its own
 * friend record (`Internals`), which `SheetRow.Handle`'s static block
 * registers once, on the class (keyed by the class, so `of(Handle)`): each wrapper below reads only its group's
 * record, so a module importing one wrapper reaches only that group.
 */
type FacadeDoor = {
  /** Build a facade: a standalone row (`standaloneFacadeOf`), a row of a sheet (`sheetFacadeOf`, `SheetRowContext.mint`). */
  readonly build: {
    standalone(inner: SheetRow, deps: SheetRowHandleDeps, rows: RowFacadePort): SheetRow.Handle;
    ofSheet(inner: SheetRow, sheet: Sheet, context: SheetRowContext, rows: RowFacadePort): SheetRow.Handle;
  };
  /** Read a facade's backing row and a standalone row's cache (`sheetRowOf`, `peekRowHandleOf`). */
  readonly backing: {
    inner(row: SheetRow.Handle): SheetRow;
    peekOwnRowHandle(row: SheetRow.Handle): Option<RowCellCache>;
  };
  /** Wire a standalone row's hooks, place a row in a joined tree (`wireSheetRowHandle`, `placeInTree`). */
  readonly wiring: {
    wireHooks(row: SheetRow.Handle, hooks: SheetRow.HandleHooks): void;
    place(row: SheetRow.Handle, joined: SheetRow.Joined): void;
  };
  /** The client's writes through a facade: a rename's formula rewrite (`beginFormulaRewrite`), a pending draft's persist (`promoteDraft`), a held write's owner. */
  readonly writes: {
    rewrite(row: SheetRow.Handle, formula: string, actor: Actor): FormulaRewrite;
    /** A pending draft's persist (`PendingRowPersist`: its buffered updates and the promote). */
    persistOf(draft: SheetRow.Handle): PendingRowPersist;
    /** The owner a held row's durable writes are watched under (`observedDurable`): its client lifecycle
     *  (`null` for a row of a joined tree no client built). */
    durableOwner(row: SheetRow.Handle): Option<ClientLifecycle>;
  };
  /** A lease's view of the row (`leasedRowOf`), its reads owned by the lease too. */
  readonly lease: {
    /** The single facade a lease's row view reads through. */
    leasedSingle(row: SheetRow.Handle, lease: (verb: string) => CellHandle.Owner): Option<SheetRow.SingleCells>;
    /** A lease's range read of the row, owned by the lease and the row. */
    leasedRangeRead(
      row: SheetRow.Handle,
      range: NaiveDate.Range | DateTime.Range<Utc>,
      lease: CellHandle.Owner,
    ): RowRangeCellHandle<SheetRow.Cell>;
  };
};

/** Building a facade (`standaloneFacadeOf`, `sheetFacadeOf`). */
const facadeBuild = new Internals<object, FacadeDoor["build"]>("SheetRow.Handle (build)");
/** A facade's backing row and own cache (`sheetRowOf`, `peekRowHandleOf`). */
const facadeBacking = new Internals<object, FacadeDoor["backing"]>("SheetRow.Handle (backing)");
/** Wiring a facade (`wireSheetRowHandle`, `placeInTree`). */
const facadeWiring = new Internals<object, FacadeDoor["wiring"]>("SheetRow.Handle (wiring)");
/** The client's writes through a facade (`beginFormulaRewrite`, `promoteDraft`, `leasedRowOf`'s durable owner). */
const facadeWrites = new Internals<object, FacadeDoor["writes"]>("SheetRow.Handle (writes)");
/** A lease's reads of a facade (`leasedRowOf`). */
const facadeLease = new Internals<object, FacadeDoor["lease"]>("SheetRow.Handle (lease)");

/**
 * **SDK-internal**: what a row facade reads of its backing row's module
 * (`sheet-row.ts`) at run time, handed to its constructor by whoever builds
 * it (`sheetRowHandleOf`, `SheetRowContext.mint`): which facade owner is a
 * sheet's row context, and the backing row's move ledger (`MoveWrites`, one
 * per backing row, shared by every facade over it). The facade module names
 * the backing row only as a type, so the two modules do not load each other.
 */
export interface RowFacadePort {
  /** `owner` as a sheet's row context (`SheetRowContext`), or `null` when it is another kind of owner. */
  contextOf(owner: Option<RowFacade.Owner>): Option<SheetRowContext>;
  /** The timepoint-cell moves in flight on `row`, made on its first move. */
  movesInFlight(row: SheetRow): MoveWrites<string, DateTime.Range<Utc>>;
}

/** **SDK-internal**: a new standalone facade over `inner` (`client.rows.open`, a pending draft). */
export function standaloneFacadeOf(inner: SheetRow, deps: SheetRowHandleDeps, rows: RowFacadePort): SheetRow.Handle {
  return facadeBuild.of(Handle).standalone(inner, deps, rows);
}

/** **SDK-internal**: a new facade over row `inner` of `sheet`, minted in `context` (`SheetRowContext.mint`). */
export function sheetFacadeOf(
  inner: SheetRow,
  sheet: Sheet,
  context: SheetRowContext,
  rows: RowFacadePort,
): SheetRow.Handle {
  return facadeBuild.of(Handle).ofSheet(inner, sheet, context, rows);
}

/**
 * **SDK-internal**: persist the pending draft `draft` (`SheetRow.Pending.promote`);
 * `adopt` runs once the POST is accepted, before anything waiting on the
 * persist hears it.
 */
export function promoteDraft(
  draft: SheetRow.Handle,
  sheetId: SheetId,
  position: Rational,
  actor: Actor,
  adopt: (created: Network.CreateRowResponse) => void,
): Promise<Network.CreateRowResponse> {
  return facadeWrites.of(Handle).persistOf(draft).promote(sheetId, position, actor, adopt);
}

/** The shared held row behind a lease's view (`leaseViewTarget`), or `row` itself. */
function sharedRow(row: SheetRow.Handle): SheetRow.Handle {
  const target = leaseViewTarget(row);
  return target instanceof Handle ? target : row;
}

/**
 * **SDK-internal** (`SheetHandle.rows`): lease `held`'s view of the shared
 * held row `row`. It IS the row — the same state, pending writes and
 * identity fields (`id`, `label`, …), `instanceof SheetRow.Handle` — except
 * that every verb that can reach the network first asks `held` (the lease's
 * check, which throws `SheetHandle.DisposedError` once that lease was
 * disposed), so a row a lease handed out refuses after that lease's
 * dispose while another lease's view of the same row keeps working.
 * Nothing is sent then: an async verb rejects, a durable verb rejects its
 * `settled`, a synchronous read of the network throws. Reads of the row's
 * own state stay allowed (the member policy: `row-lease-view.ts`).
 */
export function leasedRowOf(
  row: SheetRow.Handle,
  held: (verb: string) => void,
  leaseOwner: (verb: string) => CellHandle.Owner,
): SheetRow.Handle {
  const target = sharedRow(row);
  // A durable verb's refusal is watched under the row's durable owner as it
  // is when the refusing face (or the refusal) is made.
  const refusal = (): ((err: unknown) => unknown) => {
    const owner = facadeWrites.of(Handle).durableOwner(target);
    return (err) => observedDurable(owner, { applied: Durable.ON_SETTLE, settled: Promise.reject(err) });
  };
  // The progressive reads a lease's row starts are the lease's: owned by the
  // lease AND the sheet, so the lease's `dispose` fails and releases them
  // even while another lease keeps the sheet (and the shared row) alive.
  const single = (): Option<SheetRow.SingleReads> => {
    const face = facadeLease.of(Handle).leasedSingle(target, leaseOwner);
    return face == null ? null : leaseFace(singleReadsOf(face), held, refusal());
  };
  const writableSingle = (): Option<SheetRow.SingleCells> => {
    const face = target.capabilities().canWriteCells ? facadeLease.of(Handle).leasedSingle(target, leaseOwner) : null;
    return face == null ? null : leaseFace(face, held, refusal());
  };
  const cells = (range: NaiveDate.Range | DateTime.Range<Utc>): RowRangeCellHandle<SheetRow.Cell> => {
    held("row.cells");
    return facadeLease.of(Handle).leasedRangeRead(target, range, leaseOwner("row.cells"));
  };
  return leaseView(target, { held, own: { single, writableSingle, cells }, refusal });
}

/** The read-only face of a single facade (`row.single()`): its reads and
 *  cardinality, none of its writes. */
function singleReadsOf(face: SheetRow.SingleReads): SheetRow.SingleReads {
  return { cardinality: face.cardinality, get: face.get, peek: face.peek, cell: face.cell };
}

/**
 * **SDK-internal**: the backing `SheetRow` of a held row — its raw wire
 * state and the store writers behind it, which the row's verbs guard. An
 * application reads the row's fields through the row (`label`, `wireType`,
 * `attributes`, …).
 */
export function sheetRowOf(row: SheetRow.Handle): SheetRow {
  return facadeBacking.of(Handle).inner(row);
}

/**
 * **SDK-internal**: the shared row a lease's view of a held row forwards to
 * (`leasedRowOf`), or `row` itself — the one facade the sheet interns for
 * the row. White-box tests compare facade identity through it.
 */
export function leaseTargetOf(row: SheetRow.Handle): SheetRow.Handle {
  return sharedRow(row);
}

/**
 * **SDK-internal**: a standalone held row's `RowCellCache` if one was created
 * (a read or write made it), else `null` — never creating it (`rowHandleOf`
 * does). A row of a sheet reads the sheet's cache (`null` here).
 */
export function peekRowHandleOf(row: SheetRow.Handle): Option<RowCellCache> {
  return facadeBacking.of(Handle).peekOwnRowHandle(row);
}

/**
 * **SDK-internal** (`SheetRowTree`): place `row`'s facade in a joined tree —
 * its tree reads (`position`, `parent()`, `children()`, …) answer from
 * `joined` from now on, and a row only the rows-side sheet holds reads the
 * backing row `joined` bridged.
 */
export function placeInTree(row: SheetRow.Handle, joined: SheetRow.Joined): void {
  facadeWiring.of(Handle).place(sharedRow(row), joined);
}

/**
 * Install a standalone row's hooks: the lazy-cache init callback and
 * virtual-row parent resolution. Only the hooks present in `hooks` are set.
 * A row of a sheet resolves both through its sheet (`SheetRowContext`).
 *
 * @internal SDK-private (`RowsClient`); `lib.ts` does not export it.
 */
export function wireSheetRowHandle(
  client: SheetRow.Handle,
  hooks: SheetRow.HandleHooks,
): void {
  facadeWiring.of(Handle).wireHooks(client, hooks);
}

// ── SheetRow.Handle ─────────────────────────────────────────────

/**
 * A row's facade — ONE object per row of a sheet, whichever door reached
 * it: the sheet's row list (`handle.rows.peek`), its joined tree
 * (`handle.joinedTree().byId`, `children()`, `parent()`), a formula's
 * dependency walk. The sheet interns it per (Sheet, RowLocalId)
 * (`Sheet.facadeFor`) for the sheet's lifetime — across tree rebuilds,
 * re-keys and the COW promote (the row keeps its `RowLocalId`) — so the DOM
 * binder and the app may key state by it (`===`). `SheetRow.Joined.Handle`
 * names the same object; its tree reads (`position`, `parent()`,
 * `children()`, `dependsOn`, `surface`, …) answer from the joined tree that
 * placed it last. A standalone row (`client.rows.open`, a pending draft) is
 * a facade no sheet holds: it has no tree position, and its cells are read
 * and written through the client's own cell cache.
 *
 * Its reads are the backing row's; every write goes through ONE writer
 * (`RowWriter`): the client's row-field ledger (`RowWrites`) for its own
 * fields, the sheet's cell writer for its cells (the move ledger,
 * `MoveWrites`, for a timepoint cell the SDK's own cache holds) — the ONE
 * promote precondition (`CellAccessor.promotedRow`) first. The facade
 * announces nothing itself: the stores and the ledger do.
 */
export class Handle implements SheetRow.Cells {
  static readonly ACTOR = new Actor("sheet-row");
  /** The backing row: the registry's, or — a row only the rows-side sheet holds — the one the latest joined tree bridged. */
  #inner: SheetRow;
  /** A standalone row's client (`client.rows.open`, a pending draft); `null` for a row of a sheet. */
  readonly #deps: Option<SheetRowHandleDeps>;
  /** The sheet this row belongs to and the context it was minted in; `null` for a standalone row. */
  readonly #home: Option<{ readonly sheet: Sheet; readonly minted: SheetRowContext }>;
  /** The node of the latest joined tree that placed the row (`placeInTree`); `null` until one did. */
  #joined: Option<SheetRow.Joined> = null;
  /** A standalone row's `RowCellCache` once created (`rowHandleOf`, SDK-internal). */
  #ownRowHandle: Option<RowCellCache> = null;
  /** A standalone row's hooks (`wireSheetRowHandle`): called once when its cache is created. */
  #onRowHandleInit: Option<(rh: RowCellCache) => void> = null;
  /** A standalone row's parent resolution (`wireSheetRowHandle`); a row of a sheet resolves through its sheet. */
  #resolveParent: Option<(parentHex: string) => Option<Handle>> = null;
  readonly #virtualInfo: Option<{
    parentHex: string;
    preset: string;
  }>;
  /** The row-field writes and cell precondition of this row (`RowWriter`, the field writes' unit's). */
  readonly #writer: RowWriter;
  /** The row's own-field writes (`RowUpdates`), over its state. */
  readonly #updates: RowUpdates;
  /** What the row reads of its backing row's module: its sheet context, its move ledger. */
  readonly #rows: RowFacadePort;
  /** The row's timepoint-cell writes (`RowTimepoints`), over its state. */
  readonly #timepoints: RowTimepoints<RowCellCache>;

  // ── A pending draft's persist (`client.rows.pending`) ──────────

  /** The draft's persist (`PendingRowPersist`): built on its first pending update or promote. */
  #persist: Option<PendingRowPersist> = null;

  // One friend record per consumer group, registered on the class itself
  // (`this` here): the operations take the row they act on, so one record
  // serves every row, and building a row needs no instance to look one up.
  static {
    facadeBuild.register(this, {
      standalone: (inner, deps, rows) => new Handle(inner, deps, null, rows),
      ofSheet: (inner, sheet, context, rows) => new Handle(inner, null, { sheet, minted: context }, rows),
    });
    facadeBacking.register(this, {
      inner: (row) => sharedRow(row).#inner,
      peekOwnRowHandle: (row) => sharedRow(row).#ownRowHandle,
    });
    facadeWiring.register(this, {
      wireHooks: (view, hooks) => {
        const row = sharedRow(view);
        if (hooks.onRowHandleInit != null) row.#onRowHandleInit = hooks.onRowHandleInit;
        if (hooks.resolveParent != null) row.#resolveParent = hooks.resolveParent;
      },
      place: (row, joined) => {
        row.#inner = joined.row;
        row.#joined = joined;
      },
    });
    facadeWrites.register(this, {
      rewrite: (row, formula, actor) => sharedRow(row).#updates.beginFormulaRewrite(formula, actor),
      persistOf: (draft) => sharedRow(draft).#pendingPersist(),
      durableOwner: (row) => sharedRow(row).#durableOwner(),
    });
    facadeLease.register(this, {
      leasedSingle: (row, lease) =>
        row.#cardinality() !== "single"
          ? null
          : row.#buildSingle((verb) => {
            const own = row.#cellOwner(verb);
            return own == null ? lease(verb) : bothOwners(own, lease(verb));
          }),
      leasedRangeRead: (row, range, lease) => {
        row.#assertLive("row.cells");
        const own = row.#cellOwner("row.cells");
        return row.#rangeRead(range, own == null ? lease : bothOwners(own, lease));
      },
    });
  }

  private constructor(
    inner: SheetRow,
    deps: Option<SheetRowHandleDeps>,
    home: Option<{ readonly sheet: Sheet; readonly minted: SheetRowContext }>,
    rows: RowFacadePort,
  ) {
    this.#inner = inner;
    this.#deps = deps;
    this.#home = home;
    this.#rows = rows;
    const key = RowKey.of(inner.id.toString());
    if (key.isPending()) {
      this.#virtualInfo = null;
    } else {
      const virt = key.asVirtual();
      this.#virtualInfo = virt
        ? { parentHex: virt.parentHex, preset: virt.preset }
        : null;
    }
    this.#updates = new RowUpdates({
      correction: Handle.ACTOR,
      state: {
        row: () => this.#inner,
        key: () => this.#cellKey,
        promoted: () => this.#promotedRow(),
        position: () => this.#position(),
        editable: () => this.isEditable,
        isPending: () => this.isPending,
      },
      route: {
        writes: () => this.#writes,
        durableTransport: (rowKey) => this.#durableTransport(rowKey),
        sheetCells: () => this.#sheetCells(),
        renamed: (renamed) => this.#mirror(this.#inner.id, { renamed }),
        getRow: (rowKey) => this.#api.getResult("/api/rows/{rowKey}", { path: { rowKey } }),
      },
      nesting: {
        virtualParent: () => this.#virtualParent(),
        nestedPreset: () => nonnull(this.#virtualInfo, "virtual row").preset,
      },
      pending: {
        pendingRefusal: () => this.#home != null ? this.#pendingUnaddressable() : null,
        submitPending: (change, actor, mode) => this.#pendingPersist().submit(change, actor, mode),
      },
      local: {
        // A standalone row's cache is not minted just to drop it.
        dropCachedData: () => (this.#home == null ? this.#ownRowHandle : this.#cache())?.invalidateData(),
        rowHeightPrefs: () => this.#context()?.rowHeightPrefs ?? null,
        announce: (event) => this.#invalidation.emit(event),
      },
      failures: {
        updateOwner: () => this.#cellOwner("row.update"),
        afterFailedWrite: (error, recover) => afterFailedWrite(error, this.#cellOwner("row.update"), "row.update", recover),
      },
    });
    this.#writer = this.#updates.writer;
    this.#timepoints = new RowTimepoints<RowCellCache>({
      correction: Handle.ACTOR,
      row: {
        id: () => this.#inner.id,
        cellKey: () => this.#cellKey,
        position: () => this.#position(),
      },
      route: {
        writer: this.#writer,
        sourceWriter: () => this.#sourceWriter(),
        sheetCells: () => this.#sheetCells(),
        postCell: (rowKey, request) => this.#api.postResult("/api/rows/{rowKey}/cells", { path: { rowKey }, body: request }),
      },
      ledger: {
        moves: () => this.#rows.movesInFlight(this.#inner),
        cache: () => this.#cache(),
        showMove: (rh, from, to, id, value) => rh.optimisticMoveTimepointCell(from, to, id, value),
        takeBackMove: (rh, cell, back) => takeBackMove(rh, cell, back),
        peekTimepoint: (rh, id) => rh.peekTimepointCell(id),
        reconcile: (rh, removeId, cells, actor) => CellAccessor.reconcileAnswer(rh, removeId, cells, actor),
      },
      failures: {
        afterFailedWrite: (err, verb, recover) => afterFailedWrite(err, this.#owners, verb, recover),
        markCellsStale: () => this.#markCellsStale(),
      },
      announce: (event) => this.#invalidation.emit(event),
    });
  }

  // ── The row's context ──────────────────────────────────────────

  /**
   * The context a row of a sheet reads and writes through: the sheet's
   * newest live owner (`Sheet.ownFacades`) — a handle over the sheet —
   * else, once every owner let go, the one it was minted in (whose
   * liveness then refuses). `null` for a standalone row.
   */
  #context(): Option<SheetRowContext> {
    const home = this.#home;
    if (home == null) return null;
    const owner = home.sheet.facadeOwner;
    return this.#rows.contextOf(owner) ?? home.minted;
  }

  /** What the row writes and announces through: the client's (a standalone row's) or its sheet's. */
  get #client(): SheetRowContext.Client {
    return this.#deps ?? nonnull(this.#context(), "a row of a sheet has a context");
  }

  get #api(): TypedApi<Network.Routes> {
    return this.#client.api;
  }
  get #invalidation(): InvalidationSink {
    return this.#client.invalidation;
  }
  /** Where a subscriber's throw goes: the client's sink, else (a row no client built) rethrown. */
  get #listenerErrors(): ListenerErrorSink {
    return this.#client.listenerErrors ?? ListenerErrors.RETHROW;
  }
  get #mirror(): RowMirror {
    return this.#client.mirror;
  }
  get #writes(): RowFieldWrites {
    return this.#client.writes;
  }

  /**
   * Throw once the row's owners are gone: its sheet's
   * `SheetHandle.DisposedError` (a row of a sheet), then the client's
   * `LonaSdk.Client.DisposedError`. `verb` names the attempt. A row of a
   * joined tree no sheet owns has nothing to check.
   */
  #assertLive(verb: string): void {
    const lifecycle = this.#deps?.lifecycle ?? null;
    if (lifecycle != null) {
      lifecycle.assertLive(verb);
      return;
    }
    const sheet = this.#context()?.lifecycle ?? null;
    sheet?.assertLive(verb);
    sheet?.client.assertLive(verb);
  }

  /** The row's owners as one liveness (`#assertLive`): what a failed
   *  write's recovery checks before it starts (`afterFailedWrite`). */
  readonly #owners: AfterFailedWrite.Owner = { assertLive: (verb: string) => this.#assertLive(verb) };

  /**
   * The owner a cell handle built by `verb` checks and watches
   * (`CellHandle.Owner`): the row's sheet, else — a standalone row — the
   * client's lifecycle, so a read the client's `dispose` overtakes sends
   * nothing (or is released) and reports `LonaSdk.Client.DisposedError`.
   * `null` for a row of a joined tree no sheet owns.
   */
  #cellOwner(verb: string): Option<CellHandle.Owner> {
    const deps = this.#deps;
    if (deps != null) return CellHandle.clientOwner(deps.lifecycle, verb);
    return this.#context()?.lifecycle?.cellOwner(verb) ?? null;
  }

  /** The owner the row's durable writes are watched under (`observedDurable`): its client's lifecycle. */
  #durableOwner(): Option<ClientLifecycle> {
    return this.#deps?.lifecycle ?? this.#context()?.lifecycle?.client ?? null;
  }

  /**
   * A failed cell write's recovery (`afterFailedWrite`): the row's cells
   * are marked stale — their next read re-reads them from the server —
   * and the row's readers told, as the SDK (`SheetRow.Handle.ACTOR`).
   */
  #markCellsStale(): undefined {
    this.#cache()?.markStale();
    this.#invalidation.emit(new RowDataChanged(Handle.ACTOR, this.#inner.id, null));
    return undefined;
  }

  /**
   * The sheet's cell writer the row's cells go through (`CellAccessor`):
   * `null` for a standalone row, whose cells are the client's own cache and
   * requests, and for a joined tree built without one.
   */
  #sheetCells(): Option<JoinedCellWriter> {
    return this.#context()?.cells ?? null;
  }

  /** True if this row has not yet been persisted to the server. */
  get isPending(): boolean {
    return RowKey.of(this.#inner.id.toString()).isPending();
  }

  // ── Events ─────────────────────────────────────────────────────

  /**
   * Write-side handle for events on this row: the row's cell cache's
   * (`EventOps.fromHandle`), else — a row of a sheet with no cache of its
   * own, a virtual leaf — the client's events table's row state for the
   * rows-side variant's lookup key (`EventsClient`'s origin routing).
   * `null` when neither applies (the row produces no events).
   */
  #eventOpsCache: Option<EventOps> = null;
  get #eventOps(): Option<EventOps> {
    this.#assertLive("row.events");
    if (this.#eventOpsCache !== null) return this.#eventOpsCache;
    const rh = this.#cache();
    if (rh != null) {
      this.#eventOpsCache = EventOps.fromHandle(rh);
      return this.#eventOpsCache;
    }
    const sourceKey = this.variant?.lookupKey;
    const table = this.#context()?.eventsTable ?? null;
    if (sourceKey && table != null) {
      const state = table.getRowStateByKey(RowNetworkKey.fromWire(sourceKey));
      if (state) this.#eventOpsCache = EventOps.fromState(table, state);
    }
    return this.#eventOpsCache;
  }

  /**
   * The row's events: one accessor for reads and writes (`EventOps`),
   * built on first use and kept for the row's lifetime. Reads prefer the
   * rows-side events facade (`row.as(EVENTS_FACADE)`, the canonical derived
   * index over the store); without one (a standalone row, a row whose
   * factory declines) they derive the events from the row's cell cache
   * (`RowHandleEventsReader`), else read nothing. A row of a sheet held
   * past the sheet's `dispose` throws `SheetHandle.DisposedError` here (a
   * standalone row past `client.dispose()`, `LonaSdk.Client.DisposedError`),
   * and a `RowEvents` obtained before refuses its event writes and
   * `resolve` the same way.
   */
  #eventsCache: Option<RowEvents> = null;
  events(): RowEvents {
    this.#assertLive("row.events");
    if (this.#eventsCache != null) return this.#eventsCache;
    const variant = this.#liveVariant();
    const loader = variant
      ? (range: NaiveDate.Range, padding: Option<DateUnit>) => variant.ensureLoaded(range, padding)
      : null;
    const facade = this.as(EVENTS_FACADE);
    const rh = this.#cache();
    const reader: RowEventsReader = facade
      ? facade
      : rh
      ? new RowHandleEventsReader(rh, this.#inner.id)
      : EMPTY_ROW_EVENTS_READER;
    this.#eventsCache = new RowEvents(reader, () => this.#eventOps, loader);
    return this.#eventsCache;
  }

  // ── The rows-side model ────────────────────────────────────────

  /**
   * Typed facade lookup on the rows-side `Sheet`: `as(FACADE_KEY)` (events,
   * tasks, data, …) on the rows-side `SourceRow`, or `as(RendererClass)` —
   * the rows-side renderer subclass (`variant.defaultRenderer`), for code
   * outside the bindCell dispatch (legend, row-options, host code). `null`
   * when the row has no rows-side twin (a standalone row) or no matching
   * facade.
   */
  as<F>(key: FacadeKey<F>): Option<F>;
  as<T>(ctor: new (...args: never[]) => T): Option<T>;
  as<F, T>(keyOrCtor: FacadeKey<F> | (new (...args: never[]) => T)): Option<F | T> {
    const rowsRow = this.#liveVariant();
    if (!rowsRow) return null;
    if (typeof keyOrCtor === "function") {
      return rowsRow.defaultRenderer(keyOrCtor as new (...args: never[]) => T);
    }
    // Facades live on `SourceRow` (and its subclasses) only.
    return rowsRow instanceof SourceRow ? rowsRow.as(keyOrCtor) : null;
  }

  /**
   * The row's rows-side model row as its frozen reads face (`ModelRowReads`,
   * the face `handle.model` hands out; `RowKind.of(row.variant)` narrows it
   * to its variant's reads), never the live row: no writer is reachable
   * from it. A renderer implementor takes the live row through `liveRowOf`
   * (`@tento-lona/sheets/internal-ui`). `null` for a standalone row.
   */
  get variant(): Option<ModelRowReads> {
    const live = this.#liveVariant();
    return live == null ? null : RowKind.of(live).row;
  }

  /** The live rows-side row `variant` is the face of; `null` without one. */
  #liveVariant(): Option<ModelRow> {
    const rowsSheet = this.#context()?.rowsSheet ?? null;
    if (rowsSheet == null) return null;
    const row = rowsSheet.peekRow(this.#inner.id);
    if (row != null) return row;
    if (this.isSystemRow || this.#joined == null) return null;
    if (this.#virtualInfo) {
      const parent = rowsSheet.peekRow(
        rowsSheet.registry.resolveFromRawId(`r_${this.#virtualInfo.parentHex}`),
      );
      // Every alias row shares `descriptor.id === "alias"`; the specific
      // template (calendar-list, alias.secondary-timezones, …) lives
      // in the reconciled attributes, so match the rows-side alias on that.
      const template = Json.string(this.#inner.attributes(), "template") ?? this.#inner.descriptor.id;
      const alias = parent ? findRowsSideAliasDescendant(parent, template) : null;
      if (alias) return alias;
      return null;
    }
    throw new Error(
      `SheetRow.Handle.variant: rows-side Sheet is attached, ` +
        `but row ${this.#inner.id.rawId} (${this.#inner.descriptor.id}) is missing.`,
    );
  }

  #envelopesClient: Option<RowEnvelopes.Driver> = null;

  /**
   * This row's envelopes (`RowEnvelopes.Driver`, `@tento/lona/sheets`): `peekAll()`
   * to interrogate them, `apply(msg, actor)` to drive one — checked, the
   * intrinsic edits persisted through `update`, the attribute patch a
   * composed option wrote mirrored into this row's attributes. The one
   * write path for an envelope (a model row read off `variant` has none).
   */
  get envelopes(): RowEnvelopes.Driver {
    this.#envelopesClient ??= new RowEnvelopes.Driver(this, (verb) => this.#assertLive(verb));
    return this.#envelopesClient;
  }

  /**
   * The writers of this row's rows-side source row — when the sheet holds
   * one for it, the store the row's renderers paint from (a sticker
   * row's `canonicalCells`) — or `null`, when the row's cells live in the
   * SDK's own cell cache only (a standalone row). This row's timepoint verbs
   * (`write` of a `range` or a held timepoint cell, `move`, `replace`,
   * `clear` of a held timepoint cell) write there, so a write lands where
   * the row is painted from; the SDK's cell cache follows through the
   * rows-side mirror. SDK-private: no door hands the writers out.
   */
  #sourceWriter(): Option<SourceRow.Writer> {
    const row = this.#context()?.rowsSheet.peekRow(this.#inner.id) ?? null;
    return row instanceof SourceRow ? sourceWriterOf(row) : null;
  }

  /**
   * The timepoint cell `cell` names as this row holds it now, read from
   * the store its timepoint verbs write (`#sourceWriter`), else from its
   * cell cache; refused (`unaddressable`, `step`) when neither holds it.
   */
  #heldTimepoint(
    cell: CellTarget.TimepointCell,
    step: CellWriteError.Step,
    verb: string,
  ): CellEntry.Timepoint {
    const source = this.#sourceWriter();
    if (source != null) {
      for (const held of source.row.peekCells(null)) {
        if (held.kind === "timepoint" && held.id === cell.id) {
          return { id: held.id, dtr: held.dtr, value: held.value, style: null };
        }
      }
    }
    return heldTimepoint(this.#cache(), cell, step, verb);
  }

  /**
   * This row's alias verbs (`SheetRow.AliasCells`) when its model is an
   * alias row (`RowKind.of(row.variant).role === "alias"`), else
   * `null` — the one door onto an alias's prop write, so a row that is
   * not an alias has no verb to misuse. An `AliasRow` read off `variant`
   * carries no writer.
   */
  alias(): Option<SheetRow.AliasCells> {
    const row = this.#liveVariant();
    if (!(row instanceof AliasRow)) return null;
    return {
      setProp: async (name: string, value: Json, actor: Actor): Promise<void> => {
        this.#assertLive("row.alias.setProp");
        // A write of the row's `attributes.props` in the client's
        // row-field ledger: painted in every copy of the row and
        // announced as `actor` (`props`, `props.<name>`) before the
        // first await; a take-back is the SDK's correction, never the
        // writer's echo.
        try {
          await aliasWriterOf(row).setPropAs(name, value, actor, Handle.ACTOR);
        } catch (err) {
          // No verdict (or one the SDK could not read): the bag may have
          // landed — the row's props are re-read and shown, unless a
          // newer attribute write of the row is still in flight (its
          // answer settles the bag: the wire's PATCH replaces it whole).
          afterFailedWrite(err, this.#owners, "row.alias.setProp", () => this.#rereadAliasProps(row));
          throw err;
        }
      },
    };
  }

  /**
   * A failed `alias().setProp`'s recovery (`afterFailedWrite`): re-read
   * the row (`GET /api/rows/{rowKey}`) and show the `attributes.props`
   * bag the server holds through the alias writer
   * (`AliasRow.Writer.adoptServerProps`), announced as the SDK's
   * correction — unless a prop write of the row is still waiting then
   * (its own answer settles the bag), or the row's sheet was disposed
   * while the read was out (the answer is dropped).
   */
  async #rereadAliasProps(row: AliasRow): Promise<void> {
    const rowKey = RowNetworkKey.of(RowId.fromLocalId(this.id));
    const fresh = await SdkRequestError.checkRequest(
      "rows",
      "get",
      "GET",
      "/api/rows/{rowKey}",
      () =>
        SdkRequestError.notFoundAsNull(() =>
          this.#api.getResult("/api/rows/{rowKey}", { path: { rowKey: RowNetworkKey.forRead(rowKey) } })
        ),
    );
    if (fresh == null) return;
    if (!AfterFailedWrite.isLive(this.#owners)) return;
    const props = Json.object(Json.object(fresh.attributes ?? null)?.props ?? null);
    aliasWriterOf(row).adoptServerProps(props, Handle.ACTOR);
  }

  // ── Delegated properties ────────────────────────────────────────

  /**
   * The row's identity: the interned, branded `RowLocalId` — compared by
   * reference, keyed in `Map<RowLocalId, V>` / `Set<RowLocalId>`, and passed
   * as is to the sheet-wide reads (`handle.cells.peekInRange(row.id, …)`).
   * Its wire key is `id.toString()`.
   */
  get id(): RowLocalId {
    return this.#inner.id;
  }

  /** The row's wire key (`id.toString()`): what its requests address. */
  get #wireKey(): string {
    return this.#inner.id.toString();
  }
  /** The row type's short id (`descriptor.id`), as the DOM names it. */
  get descriptorType(): string {
    return this.#inner.descriptor.id;
  }
  get label(): Option<string> {
    return this.#inner.label;
  }
  /** Row type display name — used as EditableText placeholder when label is empty. */
  get labelPlaceholder(): string {
    return this.#inner.descriptor.label;
  }
  get formula(): Option<string> {
    return this.#inner.formula;
  }
  get lookupKey(): Option<string> {
    return this.#inner.lookupKey;
  }
  /** Canonical wire `_type` — see `SheetRow.wireType`. */
  get wireType(): string {
    return this.#inner.wireType;
  }
  get timescaleSetting(): Network.TimeScaleCustom {
    return this.#inner.timescaleSetting;
  }
  /** The row's attribute bag — see `SheetRow.attributes`. */
  attributes(): Option<JsonObject> {
    return this.#inner.attributes();
  }
  /** The row's attributes decoded by `decode` (`null` when the row has
   *  none) — see `SheetRow.attributesAs`. */
  attributesAs<A>(decode: (attributes: JsonObject) => Option<A>): Option<A> {
    return this.#inner.attributesAs(decode);
  }
  get icon(): Option<string> {
    return this.#inner.icon;
  }
  get heightPx(): Option<number> {
    return this.#inner.heightPx;
  }
  get ownerId(): UserId {
    return this.#inner.ownerId;
  }
  get isVirtual(): boolean {
    return this.#inner.isVirtual;
  }
  get parentStored(): boolean {
    return this.#inner.parentStored;
  }
  get hideLegend(): boolean {
    return this.#inner.hideLegend;
  }
  get displayLabel(): string {
    return this.#inner.displayLabel;
  }
  get rendersChildren(): boolean {
    return this.#inner.rendersChildren;
  }
  get cellLayoutOptions(): Option<CellLayoutOptions> {
    return this.#inner.cellLayoutOptions;
  }
  get descriptor(): RowTypeDescriptor {
    return this.#inner.descriptor;
  }
  /** The row's child seeds — see `SheetRow.childSpecs`. */
  get childSpecs(): readonly ChildSpec[] {
    return this.#inner.childSpecs;
  }
  /**
   * A preset-expanded row's closures (see `Graph.Hooks`): the backing
   * row's, else the rows-side twin's. Never throws — a row with no
   * rows-side twin just has none.
   */
  get graphHooks(): Option<Graph.Hooks> {
    return this.#inner.graphHooks ?? this.#context()?.rowsSheet.peekRow(this.#inner.id)?.graphHooks ?? null;
  }
  layoutSpec(sheetTimescale: Timescale): LayoutSpec {
    return this.#inner.layoutSpec(sheetTimescale);
  }
  initialScrollTop(sheetTimescale: Timescale): number {
    return this.#inner.initialScrollTop(sheetTimescale);
  }
  requiresHeaderSecondRow(sheetTimescale: Timescale): boolean {
    return this.#inner.requiresHeaderSecondRow(sheetTimescale);
  }
  headerSecondRowKind(sheetTimescale: Timescale): Option<HeaderSecondRowKind> {
    return this.#inner.headerSecondRowKind(sheetTimescale);
  }
  buildLabel(): Option<string> {
    return this.#inner.buildLabel();
  }
  buildAriaLabel(column: NaiveDate.Partial, cellValue: Option<CellValue>): Option<string> {
    return this.#inner.buildAriaLabel(column, cellValue);
  }

  /** Check if this row is of a specific type. */
  is(type: string): boolean {
    return this.#inner.descriptor.id === type;
  }

  /**
   * What the row allows (`SheetRow.capabilities`): a verb the answer
   * refuses is refused by the row's sheet too — `canDelete` false means
   * `handle.rows.delete(row, actor)` resolves `not-deletable`. On a sheet
   * the user cannot write (`isEditable` false: a viewer's shared sheet)
   * the row allows no structural edit (no reorder, reparent, duplicate or
   * delete), so a drawer offers no drag the server would refuse. Height,
   * title and cell capabilities stay the row's own: a non-editable sheet
   * keeps such edits locally.
   */
  capabilities(): Capabilities {
    const own = this.#inner.capabilities();
    if (this.isEditable) return own;
    return { ...own, canReparent: false, canDuplicate: false, canDelete: false, canReorder: false };
  }

  /** Whether the row's sheet allows edits (a standalone row: yes). */
  get isEditable(): boolean {
    const context = this.#context();
    return context == null ? true : context.editable();
  }

  // ── Tree position (the joined tree that placed the row last) ────

  /**
   * The row's node in the joined tree that placed it last
   * (`SheetRow.Joined`: its parent edge, the tree's system rows). A row no
   * joined tree placed yet (a row of a sheet read before
   * `handle.joinedTree()`, a standalone row) has none: this throws.
   */
  get joined(): SheetRow.Joined {
    const joined = this.#joined;
    if (joined == null) {
      throw new Error(
        `SheetRow.Handle.joined: row ${this.#inner.id.toString()} is in no joined tree ` +
          "(read the sheet's joinedTree first; a standalone row has no tree position)",
      );
    }
    return joined;
  }

  /** Set by the joined tree when the row is subsumed by its parent's active layout. */
  get subsumed(): boolean {
    return this.#joined?.subsumed ?? false;
  }
  get position(): Rational {
    return this.joined.position;
  }
  children(): Handle[] {
    return this.#joined?.children() ?? [];
  }
  childrenIds(): RowLocalId[] {
    return this.#joined?.childrenIds() ?? [];
  }
  parent(): Option<Handle> {
    return this.#joined?.parent() ?? null;
  }
  findAncestor<T extends ModelRow>(
    variantClass: new (...args: never[]) => T,
  ): Option<T> {
    return this.#joined?.findAncestor(variantClass) ?? null;
  }

  /**
   * DFS the self+descendant subtree for the first node whose
   * variant is a sheets-side `SourceRow`. Resolves the originating
   * data row for a rendered/aliased position — e.g. a calendar-list
   * aggregator → the first events source row underneath; a
   * rendered `timeline[column].event` row → its own variant when
   * the row IS the source. `null` when no SourceRow exists in the subtree.
   */
  rootDataRow(): Option<SourceRow> {
    const stack: Handle[] = [this];
    while (stack.length > 0) {
      const node = nonnull(stack.pop(), "rootDataRow: non-empty stack");
      const v = node.#liveVariant();
      if (v instanceof SourceRow) return v;
      for (const child of node.children()) stack.push(child);
    }
    return null;
  }

  /** Row IDs this row depends on (forward edges). */
  get dependsOn(): Set<RowLocalId> {
    return this.#joined?.dependsOn ?? new Set();
  }
  /** Row IDs that depend on this row (reverse edges). */
  get dependents(): Set<RowLocalId> {
    return this.#joined?.dependents ?? new Set();
  }
  /** Whether the row is one of the sheet's system (chrome) rows, as its joined tree lists them (a row no tree placed: its kind). */
  get isSystemRow(): boolean {
    return this.#joined?.isSystemRow ?? this.#inner.isSystemRow;
  }

  /**
   * Which surface this row belongs to, by walking up to the
   * nearest surface ancestor — a server-backed
   * `surface-container` (kind from `metadata.surface`) or an
   * authored `surface` region row (kind from
   * `attributes.surface`; the descriptor default is `"body"`).
   * Returns `"body"` for rows that have no surface ancestor
   * (i.e. top-level non-surface rows are implicitly in the body
   * surface).
   */
  get surface(): RowSection {
    let current: Option<Handle> = this;
    while (current) {
      const desc = current.descriptor;
      if (desc.role === "inert" && desc.kind === "surface-container") {
        const meta = Json.object(current.#inner.raw.metadata);
        return Json.oneOf(meta, "surface", ["header", "footer"] as const) ?? "body";
      }
      if (desc.role === "inert" && desc.kind === "surface") {
        const s = Json.string(current.attributes(), "surface");
        if (s === "header" || s === "footer") return s;
        return "body";
      }
      // A row hoisted by a surface projection (`inert/projection`
      // with `attributes.surface`) belongs to the projected
      // surface even though it is not re-parented under the surface
      // container in the live tree (the body view tree hoists it
      // for layout, but `parent()` still points at the projection).
      // Honour the projection's target so surface-gated renderers —
      // e.g. the footer timeline-legend's tick marks — fire for
      // projected rows. A `surface=body` (or unset) projection just
      // routes inline, so keep walking in that case. Matched on
      // `kind` alone: synthesized data-preset descriptors don't
      // carry `role` on the joined client layer.
      if (desc.kind === "projection" || desc.id === "projection") {
        const s = Json.string(current.attributes(), "surface");
        if (s === "header" || s === "footer") return s;
      }
      current = current.parent();
    }
    return "body";
  }

  /**
   * Depth-first walk over `this` + descendants. Returns the first
   * row (self or descendant) whose `as(facadeKey)` is non-null.
   */
  findRowWithFacade<F>(facadeKey: FacadeKey<F>): Option<Handle> {
    if (this.as(facadeKey)) return this;
    for (const child of this.children()) {
      const found = child.findRowWithFacade(facadeKey);
      if (found) return found;
    }
    return null;
  }

  /**
   * Depth-first walk over `this` + descendants. Returns the first
   * facade for `facadeKey` (or null) without requiring the caller
   * to chain `.as()` after `findRowWithFacade`.
   */
  findFacade<F>(facadeKey: FacadeKey<F>): Option<F> {
    const facade = this.as(facadeKey);
    if (facade) return facade;
    for (const child of this.children()) {
      const found = child.findFacade(facadeKey);
      if (found) return found;
    }
    return null;
  }

  /**
   * Descendant leaf rows (no children of their own match) where
   * `as(facadeKey)` is non-null. A leaf is defined as: the row has
   * the facade, but none of its children do.
   */
  findLeafRowsWithFacade<F>(facadeKey: FacadeKey<F>): Handle[] {
    const descendants = this.children().flatMap(
      (child) => child.findLeafRowsWithFacade(facadeKey),
    );
    if (descendants.length > 0) return descendants;
    return this.as(facadeKey) ? [this] : [];
  }

  // ── Cell access ─────────────────────────────────────────────────

  /** SDK-internal: the row's `RowCellCache` (`rowHandleOf`). */
  [ROW_HANDLE](): RowCellCache {
    return nonnull(this.#cache(), `row ${this.#wireKey}: a joined tree built without a cell accessor has no cell cache`);
  }

  /**
   * The row's cell cache: a row of a sheet's is the sheet's (the joined
   * tree's, or the sheet's cell writer's under the row's cell-access key —
   * `null` for a tree built without one); a standalone row's is the
   * client's, created lazily (`#ensureRowHandle`).
   */
  #cache(): Option<RowCellCache> {
    if (this.#home == null) return this.#ensureRowHandle();
    if (this.#joined != null) return this.#joined.rowHandle;
    const cells = this.#sheetCells();
    return cells == null
      ? null
      : cells.rowHandle(RowNetworkKey.fromWire(cellAccessKey(this.#wireKey, this.lookupKey)));
  }

  /**
   * A standalone row's cache, created lazily through the client-scoped
   * backend registry (so every consumer of the row converges on ONE
   * `RowCellCache`); fires `onRowHandleInit` once. A renderer-scoped
   * virtual row (`virtual(r_<hex>|renderer)`) projects its parent source
   * row's cells: its cache delegates to the parent's.
   */
  #ensureRowHandle(): RowCellCache {
    const deps = nonnull(this.#deps, "a standalone row's cache");
    if (!this.#ownRowHandle) {
      const rowId = RowId.of(this.#wireKey);
      const accessKey = cellAccessKey(this.#wireKey, this.lookupKey);
      this.#ownRowHandle = deps.handleResolver(RowId.of(accessKey));
      if (rowId.isVirtual()) {
        const virt = this.#virtualInfo;
        if (virt && virt.preset === "renderer" && this.#resolveParent) {
          const parentClient = this.#resolveParent(virt.parentHex);
          if (parentClient) this.#ownRowHandle.bindSourceHandle(parentClient.#ensureRowHandle());
        }
      }
      this.#onRowHandleInit?.(this.#ownRowHandle);
    }
    return this.#ownRowHandle;
  }

  /**
   * Every cell this row holds over `range`, date-keyed and timepoint
   * alike, as one progressive read (`onLoading` / `onData` /
   * `onDataFull`, or `await`). Each entry carries its own address: a
   * `kind: "date"` entry its `date`, a `kind: "timepoint"` entry its `dtr`
   * — nothing is converted from one to the other, so a caller that wants
   * only one kind filters by `kind`. A `NaiveDate.Range` reads the
   * date-keyed cells of its days and the timepoint cells within their
   * UTC bounds; a `DateTime.Range<Utc>` reads the timepoint cells within
   * it and the date-keyed cells of every day from its first to its last
   * date. An alias row reads its source row. Pass an entry to
   * `CellTarget.cell` to write it. A refused or unanswered read rejects
   * with its `SdkRequestError` (and reaches `onError`). A row of a sheet
   * held past the sheet's `dispose` throws `SheetHandle.DisposedError`
   * here; a standalone row held past `client.dispose()` throws
   * `LonaSdk.Client.DisposedError`, and a read the owner's disposal
   * overtakes rejects with it.
   */
  cells(range: NaiveDate.Range | DateTime.Range<Utc>): RowRangeCellHandle<SheetRow.Cell> {
    this.#assertLive("row.cells");
    return this.#rangeRead(range, this.#cellOwner("row.cells"));
  }

  /** The progressive range read of `range`, owned by `owner`. */
  #rangeRead(
    range: NaiveDate.Range | DateTime.Range<Utc>,
    owner: Option<CellHandle.Owner>,
  ): RowRangeCellHandle<SheetRow.Cell> {
    const span = CellWindow.of(range);
    const rh = this.#cache();
    if (AliasResolver.is(this.#inner.descriptor.dataOwner) && this.#home != null) {
      const sourceRh = rh?.sourceHandle;
      if (!sourceRh) {
        throw new Error(`Alias resolver row ${this.id.toString()} has no source RowCellCache.`);
      }
      return CellWindow.read(this.id, this.#wireKey, span, this.#api, sourceRh, owner, this.#listenerErrors);
    }
    return CellWindow.read(this.id, this.#wireKey, span, this.#api, rh, owner, this.#listenerErrors);
  }

  /**
   * Generate a cell value server-side (AI/auto-generate). Throws
   * `CellWriteError("generate-cell")` when the server refuses or does not
   * answer.
   */
  async generateCell(date: NaiveDate.Partial): Promise<Option<Json>> {
    this.#assertLive("row.generateCell");
    let cell: Network.SheetCell;
    try {
      cell = await CellWriteError.check(
        "generate-cell",
        "POST",
        "/api/rows/{rowKey}/cells/generate",
        () =>
          this.#api.postResult("/api/rows/{rowKey}/cells/generate", {
            path: { rowKey: RowNetworkKey.of(RowId.fromLocalId(this.id)) },
            body: { date: date.toString() },
          }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the server stores
      // the cell it generates, so it may hold one — the row's cells are
      // marked stale and re-read.
      afterFailedWrite(err, this.#owners, "row.generateCell", () => {
        this.#cache()?.markStale();
        return undefined;
      });
      throw err;
    }
    return cell.data;
  }

  /**
   * Write `data` to the cell `target` addresses, checked. A date-keyed
   * target (`date`, or a held `CellEntry` — its `id`, when it has one, is
   * the upsert key) on a row of a sheet goes through the sheet's cell
   * writer (`CellAccessor.set`): COW-promote an ephemeral sheet, persist a
   * lookup-seeded row, write, mirror into the row's cache, re-evaluate
   * formula dependents; a standalone row (`client.rows.open`) has no sheet
   * to promote and no formula graph, so it persists and mirrors only. A
   * timepoint target (`range`, or a held `TimepointCellEntry`, replaced in
   * place through the server's atomic replace) is written through the
   * rows-side source row's store the row is painted from (a row of a
   * sheet; the sheet's cell writer when it has none), else posted and the
   * server's answer reconciled into the row's cache; its days are
   * repainted. An `add` target (a multi-cardinality row's new date-keyed
   * cell) is posted without an id and the server's answer — which carries
   * the id it minted — reconciled into the row. A reserved row opened
   * through `client.rows.open(":~lona:stickers")` is writable this way by
   * lookup key. `handle.cells` is read-only: this is the one cell write
   * path. The sheet's COW promote comes first for every write (`RowWriter`).
   * @param actor who wrote it (`Actor.EXTERNAL` for a caller with no echo
   *              of its own to recognise): the mirrored cell records it
   *              (`CellHandle.onData`'s `change.actor`).
   * @throws CellWriteError (step `write-cell`, or `write-timepoint-cell` for a
   *              timepoint target) when the server rejects the write or
   *              does not answer; nothing is mirrored then. Outcome
   *              `unaddressable`, nothing sent, for a `date` target on a
   *              multi-cardinality row, and for an `add` or a cell that
   *              names an id (`CellTarget.keyed`) on a single-cardinality
   *              one. Once the server accepted the write it never rejects.
   */
  async #write(target: CellTarget, data: Json, actor: Actor): Promise<void> {
    this.#assertLive("row.write");
    onRow(target, this.#inner, "write");
    switch (target.kind) {
      case "date":
        this.#checkAddress("date", null, "write-cell", "row.write");
        await this.#writeDated(target.date, data, null, actor);
        return;
      case "add":
        this.#checkAddress("add", null, "write-cell", "row.write");
        await this.#addDated(target.date, data, actor);
        return;
      case "add-with-id":
        // The server upserts by the chosen id: the create is a write of it.
        this.#checkAddress("add", target.id, "write-cell", "row.write");
        await this.#writeDated(target.date, data, target.id, actor);
        return;
      case "range":
        await this.#timepoints.writeTimepoint(target.range, data, null, actor);
        return;
      case "timepoint-cell": {
        // Replaced in place at the range the row holds it at now.
        const held = this.#heldTimepoint(target.cell, "write-timepoint-cell", "row.write");
        await this.#timepoints.writeTimepoint(held.dtr, data, held.id, actor);
        return;
      }
      case "dated-cell":
        this.#checkAddress("date", target.cell.id, "write-cell", "row.write");
        await this.#writeDated(target.cell.date, data, target.cell.id, actor);
        return;
    }
  }

  /**
   * Refuse a date-keyed address this row's cardinality cannot honour
   * (`CellAccessor.checkAddress`: an id-less date on a multi-cardinality
   * row; an `add`, or an id, on a single-cardinality one) before anything
   * is sent. The cardinality is the row's cell cache's once it has one,
   * else its type descriptor's (what the cache derives it from): the
   * check mints no cache.
   */
  #checkAddress(
    address: "date" | "add",
    id: Option<string>,
    step: CellWriteError.Step,
    verb: string,
  ): void {
    CellAccessor.checkAddress(this.#cardinality(), address, id, step, verb);
  }

  /** The row's cardinality: its cell cache's once it has one, else its
   *  type descriptor's (what the cache derives it from) — none minted. */
  #cardinality(): CellCardinality {
    const rh = this.#home == null ? this.#ownRowHandle : this.#cache();
    return rh?.cardinality ?? this.#inner.descriptor.cellCardinality;
  }

  /**
   * Write `data` to a date-keyed cell through the client's debounce
   * executor: the service worker owns the wire write across reloads,
   * coalescing rapid edits (the text-editor commit path). Returns at once
   * (`Durable`): `settled` resolves when the execution that carried the
   * write answered without refusing it (the cell is then mirrored), and
   * rejects — nothing mirrored — with the failure before or at the
   * hand-off (the sheet's COW promote, the row persist) or with
   * `CellWriteError` when the write was refused or went unanswered. A row
   * of a sheet hands it to the sheet's cell writer (promote, persist a
   * seeded row, mirror, formulas). The caller owns `settled`.
   * Only a date-keyed address (`CellTarget.Dated`) compiles: the server
   * mints a timepoint write's id, which a debounced hand-off never
   * returns. An untyped caller's timepoint (or `add`) address rejects
   * `settled` with `CellWriteError` (outcome `unaddressable`), nothing
   * sent — as does a `date` target on a multi-cardinality row. A durable
   * verb never throws: every refusal, a disposed row's included, reaches
   * the caller through `settled` (`applied` is then `on-settle`). Mirrors
   * after: nothing paints before the verdict.
   */
  #writeDurable(target: CellTarget.DateKeyed, data: Json, actor: Actor): Durable<void> {
    return reportThroughSettled(this.#durableOwner(), () => {
      this.#assertLive("row.writeDurable");
      onRow(target, this.#inner, "writeDurable");
      const address = durableAddress(target, "writeDurable");
      this.#checkAddress("date", address.id, "write-cell", "row.writeDurable");
      return {
        applied: Durable.ON_SETTLE,
        settled: this.#writeDatedDurable(address.date, data, address.id, actor),
      };
    });
  }

  /**
   * Move a held timepoint cell to `to`. The target names the cell by its
   * id alone: its value and current range are read from the row's store
   * (so a stale entry never re-posts an old value), and an id the row
   * does not hold rejects with `CellWriteError` (step `move-cell`,
   * outcome `unaddressable`), nothing sent. Paints first: settled locally
   * at the new range (synchronous repaint), then persisted through the
   * server's atomic replace (the cell's id) of that value. When the
   * server refuses or does not answer, the move is taken back and the
   * write's `CellWriteError("write-timepoint-cell")` is rethrown.
   * Overlapping moves of one cell follow the one move ledger: a refused
   * move takes back only its own move — the cell shows the newest
   * surviving move, else where it was — on every surface the row is
   * painted from (the rows-side store of a row of a sheet, whose
   * timepoint writes settle the move; the SDK's cell cache of a row with
   * none, `MoveWrites`), and a refused move a newer one superseded
   * changes nothing, so it never resurrects a cell the newer move's
   * replace deleted. The move and the accepted write are stamped with
   * `actor`; the take-back is announced as the SDK's correction
   * (`SheetRow.Handle.ACTOR`), never as the writer — a surface that
   * painted its own drag and skips its own echo must still repaint the
   * cell back. Only a held timepoint cell (`CellTarget.OnTimepointCell`)
   * compiles; an untyped caller's date-keyed cell rejects with
   * `CellWriteError` (step `move-cell`, outcome `unaddressable`).
   */
  #moveCell(target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, actor: Actor): Promise<void> {
    return this.#relocate(target, to, null, actor, "move");
  }

  /**
   * Replace a held timepoint cell with `data` at `to` in one write — a move
   * that also changes the cell's value (a sticker dragged to another spot
   * whose position lives in its data). The cell is named by its id alone,
   * as `move` names it (an id the row does not hold rejects with
   * `CellWriteError`, step `move-cell`, outcome `unaddressable`, nothing
   * sent). Paints first: the cell shows `data` at `to` at once, and the
   * write is the server's atomic replace (the cell's id) of `data` at `to`.
   * A refusal, or no answer, takes it back exactly as a refused `move` is
   * taken back (the cell returns to where the newest surviving move or
   * replace put it, with the value it held before this replace; announced
   * as `SheetRow.Handle.ACTOR`), and the write's
   * `CellWriteError("write-timepoint-cell")` is rethrown.
   */
  #replaceCell(
    target: CellTarget.OnTimepointCell,
    to: DateTime.Range<Utc>,
    data: Json,
    actor: Actor,
  ): Promise<void> {
    return this.#relocate(target, to, { data }, actor, "replace");
  }

  /** `move` (`replacement: null`: the cell's own value) and `replace`: the
   *  held cell resolved here, the write the row's timepoint writes'. */
  async #relocate(
    target: CellTarget.OnTimepointCell,
    to: DateTime.Range<Utc>,
    replacement: Option<{ readonly data: Json }>,
    actor: Actor,
    verb: "move" | "replace",
  ): Promise<void> {
    this.#assertLive(`row.${verb}`);
    onRow(target, this.#inner, verb);
    const cell = this.#heldTimepoint(timepointOf(target, verb), relocateStep(verb), `row.${verb}`);
    await this.#timepoints.relocate(cell, to, replacement, actor, verb);
  }

  /**
   * Clear the cell `target` addresses, checked. A date-keyed target is
   * written empty, as `write` writes it (a held id-keyed cell is deleted
   * by its id); a held timepoint cell is deleted by its id and its days
   * repainted. `actor` as for `write`.
   * @throws CellWriteError (step `clear-cell`, or `clear-timepoint-cell` for
   *   a timepoint cell) when the server refuses or does not answer; the
   *   cell stays. A `range` addresses no existing cell and does not
   *   compile; from an untyped caller it rejects with outcome
   *   `unaddressable`, nothing sent.
   */
  async #clear(target: CellTarget.Dated | CellTarget.OnCell, actor: Actor): Promise<void> {
    this.#assertLive("row.clear");
    onRow(target, this.#inner, "clear");
    const address = existingAddress(target, "clear");
    switch (address.kind) {
      case "date":
        this.#checkAddress("date", null, "clear-cell", "row.clear");
        await this.#writeDated(address.date, null, null, actor);
        return;
      case "timepoint-cell":
        await this.#timepoints.deleteTimepoint(address.cell, actor);
        return;
      case "dated-cell":
        this.#checkAddress("date", address.cell.id, "clear-cell", "row.clear");
        await this.#writeDated(address.cell.date, null, address.cell.id, actor);
        return;
    }
  }

  /**
   * The row's one-cell reads on a single-cardinality row — one cell per
   * column, read by its day (`get` / `peek` / `cell`) — or `null` when the
   * row is multi-cardinality (its cells are told apart by id: read them
   * with `cells(range)`). Read-only, on a computed row too; the writes are
   * `writableSingle()`'s. The row's cardinality is its cell cache's once
   * it has one, else its type descriptor's.
   */
  single(): Option<SheetRow.SingleReads> {
    const face = this.#singleFaceOf();
    return face == null ? null : this.#singleReads ??= singleReadsOf(face);
  }

  /** The single facade whatever the row's writability (`single()` reads it). */
  #singleFaceOf(): Option<SheetRow.SingleCells> {
    if (this.#cardinality() !== "single") return null;
    return this.#singleFace ??= this.#buildSingle((verb) => this.#cellOwner(verb));
  }

  /**
   * The row's cell verbs narrowed to a single-cardinality row whose cells
   * take writes — one cell per column, addressed by its date
   * (`CellTarget.date`) — or `null` when the row is multi-cardinality
   * (use `writableMulti()`) or its cells are computed
   * (`capabilities().canWriteCells` false: a formula row, a client-side
   * provider's row — read those with `single()`). The facade's verbs are
   * the row's.
   */
  writableSingle(): Option<SheetRow.SingleCells> {
    if (!this.#inner.capabilities().canWriteCells) return null;
    return this.#singleFaceOf();
  }

  /** The single facade, its reads owned by `owner` (the row's, or a
   *  lease's and the row's: `leasedRowOf`). */
  #buildSingle(owner: (verb: string) => Option<CellHandle.Owner>): SheetRow.SingleCells {
    const reads = singleCellReads(
      this.id,
      () => this.#cache(),
      (verb) => this.#assertLive(verb),
      owner,
    );
    return {
      cardinality: "single" as const,
      ...reads,
      write: (target: CellTarget.Single, data: Json, actor: Actor) => this.#write(target, data, actor),
      writeDurable: (target: CellTarget.Dated, data: Json, actor: Actor) => this.#writeDurable(target, data, actor),
      clear: (target: CellTarget.Dated | CellTarget.OnTimepointCell, actor: Actor) => this.#clear(target, actor),
      clearDurable: (target: CellTarget.Dated, actor: Actor) => this.#clearDurable(target, actor),
      moveCell: (target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, actor: Actor) => this.#moveCell(target, to, actor),
      replaceCell: (target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, data: Json, actor: Actor) =>
        this.#replaceCell(target, to, data, actor),
    };
  }

  /**
   * The row's cell verbs narrowed to a multi-cardinality row whose cells
   * take writes — cells told apart by id, a new one added with
   * `CellTarget.add` — or `null` when the row is single-cardinality (one
   * cell per column: use `writableSingle()`) or its cells are computed
   * (`capabilities().canWriteCells` false). The facade's verbs are the
   * row's.
   */
  writableMulti(): Option<SheetRow.MultiCells> {
    if (this.#cardinality() !== "multi" || !this.#inner.capabilities().canWriteCells) return null;
    return this.#multiFace ??= {
      cardinality: "multi" as const,
      write: (target: CellTarget.Multi, data: Json, actor: Actor) => this.#write(target, data, actor),
      writeDurable: (target: CellTarget.MultiDated, data: Json, actor: Actor) => this.#writeDurable(target, data, actor),
      clear: (target: CellTarget.OnCell, actor: Actor) => this.#clear(target, actor),
      clearDurable: (target: CellTarget.OnDatedCell, actor: Actor) => this.#clearDurable(target, actor),
      moveCell: (target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, actor: Actor) => this.#moveCell(target, to, actor),
      replaceCell: (target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, data: Json, actor: Actor) =>
        this.#replaceCell(target, to, data, actor),
    };
  }

  /** The narrowed facades, built on first use (`single()`,
   *  `writableSingle()`, `writableMulti()`). */
  #singleFace: Option<SheetRow.SingleCells> = null;
  #singleReads: Option<SheetRow.SingleReads> = null;
  #multiFace: Option<SheetRow.MultiCells> = null;

  /** `clearDurable` of any date-keyed address (the single facade's `date` too). */
  #clearDurable(target: CellTarget.DateKeyed, actor: Actor): Durable<void> {
    return reportThroughSettled(this.#durableOwner(), () => {
      this.#assertLive("row.clearDurable");
      onRow(target, this.#inner, "clearDurable");
      const address = durableAddress(target, "clearDurable");
      this.#checkAddress("date", address.id, "clear-cell", "row.clearDurable");
      return {
        applied: Durable.ON_SETTLE,
        settled: this.#writeDatedDurable(address.date, null, address.id, actor),
      };
    });
  }

  /** The row's wire key for a cell write through the sheet's cell writer. */
  get #cellKey(): RowNetworkKey {
    return RowNetworkKey.of(this.id);
  }

  /** Where the row sits in its sheet (a lookup-seeded row is persisted there): the placing tree's, else the sheet's. */
  #position(): Option<Rational> {
    return this.#joined?.position ?? this.#context()?.positionOf(this.id) ?? null;
  }

  /**
   * Add a date-keyed cell to this multi-cardinality row: the sheet's cell
   * writer, else posted without an id (the server mints it) and the
   * server's answer reconciled into the row's cache (`reconcileAnswer`),
   * as a `range` write is; the row's `RowDataChanged` stamped `actor`.
   */
  async #addDated(date: NaiveDate.Partial, data: Json, actor: Actor): Promise<void> {
    const cells = this.#sheetCells();
    if (cells != null) {
      await cells.add(this.#cellKey, date, data, this.#position(), actor);
    } else {
      let answer: Network.CellWriteResponse;
      try {
        answer = await this.#writer.cell(() =>
          CellAccessor.persistCell(this.#api, RowNetworkKey.fromWire(this.#wireKey), date, data, null)
        );
      } catch (err) {
        afterFailedWrite(err, this.#owners, "row.write", () => this.#markCellsStale());
        throw err;
      }
      CellAccessor.reconcileAnswer(nonnull(this.#cache(), "a standalone row's cache"), null, answer.cells, actor);
    }
    this.#invalidation.emit(new RowDataChanged(actor, this.#inner.id, null));
  }

  /** The checked date-keyed write: the sheet's cell writer, else persist and mirror. */
  async #writeDated(
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    actor: Actor,
  ): Promise<void> {
    const cells = this.#sheetCells();
    if (cells != null) {
      await cells.set(this.#cellKey, date, data, id, this.#position(), actor);
      return;
    }
    try {
      await this.#writer.cell(() =>
        CellAccessor.persistCell(this.#api, RowNetworkKey.fromWire(this.#wireKey), date, data, id)
      );
    } catch (err) {
      // Nothing mirrored: a write that may have landed is missing from
      // the cache until the row's cells are re-read.
      afterFailedWrite(err, this.#owners, data == null ? "row.clear" : "row.write", () => this.#markCellsStale());
      throw err;
    }
    CellAccessor.mirrorWrite(nonnull(this.#cache(), "a standalone row's cache"), date, data, id, actor);
  }

  /**
   * The durable date-keyed write: the sheet's cell writer, else handed to
   * the client's debounce executor under the cell's durable key
   * (`CellAccessor.durableCellKey`) and mirrored once it answered.
   */
  async #writeDatedDurable(
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    actor: Actor,
  ): Promise<void> {
    const cells = this.#sheetCells();
    if (cells != null) {
      await cells.setDurable(this.#cellKey, date, data, id, this.#position(), actor);
      return;
    }
    const deps = nonnull(this.#deps, "a standalone row's client");
    CellAccessor.requireCellId("SheetRow.Handle.writeDurable", id);
    const rowKey = RowNetworkKey.fromWire(this.#wireKey);
    const durable = deps.durableWrites;
    const requestId = durable.nextRequestId();
    const failure = await durable.handOff(
      CellAccessor.durableCellKey(rowKey, date, id),
      requestId,
      () => CellAccessor.persistCellDurable(this.#api, rowKey, date, data, id, requestId),
    );
    if (failure != null) {
      const err = CellAccessor.durableRefusal(data == null ? "clear-cell" : "write-cell", failure);
      afterFailedWrite(err, this.#owners, data == null ? "row.clearDurable" : "row.writeDurable", () =>
        this.#markCellsStale());
      throw err;
    }
    CellAccessor.mirrorWrite(nonnull(this.#cache(), "a standalone row's cache"), date, data, id, actor);
  }

  /**
   * The ONE precondition of every write this row sends (`RowWriter`'s
   * first step, cells and rows): on a row of an unpromoted (ephemeral)
   * sheet, the sheet's COW promote awaited (`CellAccessor.promotedRow`) —
   * it re-keys the row, so the answered key is the row's CURRENT wire key.
   * A synced sheet's row, a standalone row and a row of a joined tree
   * built without a cell writer answer their key at once, so the write is
   * sent in the turn it was issued, keeping the order of the row's writes
   * on the wire.
   */
  #promotedRow(): Option<Promise<RowNetworkKey>> {
    return this.#sheetCells()?.promotedRow(this.#cellKey) ?? null;
  }

  // ── Mutations ───────────────────────────────────────────────────

  /**
   * Update row properties, CHECKED — the `update` of events and tasks.
   * `patch` is a `RowPatch`: the fields the row paints and takes back
   * (`label`, `icon`, `heightPx`, `formula` — each set, or removed with
   * `Patch.clear` —, `timeScale`, set-only, and the whole `attributes`
   * bag); the lookup key is `setLookupKey`, part of the bag
   * `mergeAttributes`. The write enters the client's ONE row-field ledger
   * (`RowWrites`, shared by every copy of the row — this facade, the
   * rows-side writers — and every sheet holding it) through the row's one
   * writer (`RowWriter`): it is shown in every copy of the row at once,
   * synchronously before this returns its promise, each changed field
   * announced once (`RowHeightChanged`, `RowLegendChanged`,
   * `RowDataChanged`), stamped with `actor` (`Actor.EXTERNAL` for a caller
   * with no echo of its own to recognise); then — the sheet's COW promote
   * first, and a lookup-seeded row persisted — `PATCH /api/rows/{rowKey}`
   * is sent through the one row transport (`Backend.updateRow`) and
   * awaited, its body the row's projection of the fields this update
   * writes WHEN it is sent. A parent-stored virtual row (its fields kept
   * in its parent's `attributes.nestedRows`) paints the same way, the
   * parent's update awaited. A refused or unanswered PATCH takes back only
   * its OWN contribution (announced as the SDK's correction,
   * `SheetRow.Handle.ACTOR` — never as `actor`, so a surface that skips its
   * own echo still repaints the take-back) and rejects with
   * `RowWriteError("update")`: the fields it wrote go back to the row's
   * confirmed state with every other update still standing replayed in
   * issue order, so a refused older update never erases a newer one that
   * landed; and when a later PATCH carried its fields, the surviving
   * projection is PATCHed again so the server does not keep them. When a
   * data-affecting attribute changed (`descriptor.dataKeys`), or the server
   * says the row's cells changed with the write (`shouldRefetchData`), the
   * row's cached cells are re-fetched — and the attribute invalidations
   * emitted — only after the server accepted the PATCH. On a non-editable
   * sheet a height change is kept in the local row-height preferences
   * instead (nothing is sent).
   *
   * On a pending draft (`client.rows.pending`, not yet persisted) the
   * change is buffered and applied at once, and the promise resolves only
   * once the server holds it: when `promote` succeeds with the change in
   * its POST, or — for a change buffered after the POST was sent — once a
   * PATCH of the created row lands; it rejects with the promote's (or
   * that PATCH's) `RowWriteError`, the change taken back first (announced
   * as `SheetRow.Handle.ACTOR`): a refused promote leaves nothing of it on
   * the row or in what the next promote's POST carries. A pending row of a
   * sheet has no persist of its own to wait on: the update rejects at once
   * with `RowWriteError("update")`, outcome `"unaddressable"`, and changes
   * nothing. A new label also rewrites and PATCHes every formula of an open
   * sheet that names the row by its old label (`rows["old"]`): painted
   * before the label and taken back with it (one write group); a rewrite
   * refused after the label landed is taken back and rejects this update
   * with its `RowWriteError` (the label stands). For an edit that must not
   * wait on the server (a live drag), use `updateDurable`.
   */
  async update(
    patch: RowPatch,
    actor: Actor,
  ): Promise<void> {
    this.#assertLive("row.update");
    await this.#updates.update(RowWrites.set(RowPatch.encode(patch)), actor);
  }

  /**
   * Merge `patch` into the row's attribute bag, CHECKED — the partial
   * attribute write (`update({ attributes })` replaces the whole bag). It
   * DEEP-MERGES (a key it names replaces that key's value, plain objects
   * recursing; every other key stays) into the bag the row shows — the
   * client's one projection of the row (`RowWrites`: its confirmed bag with
   * every attribute write still in flight laid over it, whichever facade or
   * sheet issued it) — and sends that WHOLE projected bag when the PATCH is
   * sent: the server's row PATCH replaces `attributes` wholesale, so the bag
   * sent is exactly the bag painted, and never a stale copy that drops a
   * write another writer made. A refusal takes back this merge alone (and
   * PATCHes the surviving bag again when a later PATCH carried it), so once
   * every write settled the row and the server hold the confirmed bag with
   * exactly the accepted merges laid over it, in issue order. Paints first;
   * a refusal is announced as `SheetRow.Handle.ACTOR` and rejects with
   * `RowWriteError("update")`.
   */
  async mergeAttributes(patch: JsonObject, actor: Actor): Promise<void> {
    this.#assertLive("row.mergeAttributes");
    await this.#updates.update(RowWrites.mergeAttributes("deep", patch, []), actor);
  }

  /**
   * Set the row's lookup key to `key`, CHECKED: the row is re-registered
   * under it at once — `handle.rows.peek({ lookupKey })` and
   * `client.rows.open(key)` find it, the old key no longer names it — and
   * the PATCH sent. A refusal (or no answer) puts the old key back (the row
   * is re-registered under it; announced as `SheetRow.Handle.ACTOR`) and
   * rejects with `RowWriteError("update")`. It is a write of the row-field
   * ledger like `update`, announced the same through every door onto the
   * row: on a pending draft it is buffered and carried by the promote's
   * POST.
   */
  async setLookupKey(key: RowKey.Keyed, actor: Actor): Promise<void> {
    this.#assertLive("row.setLookupKey");
    if (this.isVirtual && this.parentStored) {
      // Its parent's attributes store it, and they carry no lookup key:
      // refused before the row or the registry changes, nothing sent.
      throw new RowWriteError({
        step: "update",
        method: "PATCH",
        path: "/api/rows/{rowKey}",
        failure: { outcome: "unaddressable" },
        details: "row.setLookupKey: a virtual row stored in its parent's attributes has no lookup key of its own " +
          "to persist — key the parent row",
      });
    }
    await this.#updates.update(RowWrites.set({ lookupKey: key.toString() }), actor);
  }

  /** The durable transport of this row's writes: the sheet's cell writer, else the client's debounce executor. */
  #durableTransport(rowKey: RowNetworkKey): RowWrites.Transport {
    const cells = this.#sheetCells();
    if (cells != null) return (body) => cells.patchRowDurable(rowKey, body).then(() => RowWrites.ANSWERED);
    const deps = nonnull(this.#deps, "a standalone row's client");
    return (body) =>
      DurableHandoffs.persistRowPatch(this.#api, deps.durableWrites, rowKey, body).then(() => RowWrites.ANSWERED);
  }

  /**
   * Update row properties DURABLY: applied and announced at once like
   * `update` (the same row-field ledger, the same writer), and the PATCH
   * handed to the client's debounce executor (the service worker owns it
   * across reloads and coalesces rapid patches of the same fields of the
   * row — `DurableHandoffs.rowPatchKey`) once the sheet's COW promote
   * landed (and a lookup-seeded row persisted), its body the row's
   * projection of the fields when it is handed off. Returns at once
   * (`Durable`): `settled` resolves when the execution that carried the
   * patch answered without refusing it, and rejects with
   * `RowWriteError("update")` when it was refused or went unanswered — its
   * own contribution taken back as `update` takes it back, and the row
   * re-read from the server by the client, so the refused change does not
   * stand. When a data-affecting attribute changed, the cells are
   * re-fetched once `settled` resolves (the server then holds the new
   * attributes). The caller owns `settled`. On a pending draft, `settled`
   * waits for the row's persist exactly as `update` does, the follow-up
   * PATCH (a change buffered after `promote`'s POST was sent) handed to
   * the debounce executor, and a refused promote takes the change back
   * before `settled` rejects; on a pending row of a sheet nothing is
   * applied (`applied.kind === "on-settle"`) and `settled` rejects at once
   * as `update` does. Paints first (`applied` is `shown`); a refusal found
   * before anything is sent (a disposed row) is reported through `settled`
   * too, never thrown.
   */
  updateDurable(
    patch: RowPatch,
    actor: Actor,
  ): Durable<void> {
    return this.#updateDurable(RowWrites.set(RowPatch.encode(patch)), actor);
  }

  /** `updateDurable` of a ledger change. */
  #updateDurable(
    change: RowWrites.Change,
    actor: Actor,
  ): Durable<void> {
    return reportThroughSettled(this.#durableOwner(), () => {
      this.#assertLive("row.updateDurable");
      const { shown, settled } = this.#updates.durable(change, actor);
      return { applied: shown ? Durable.shown(undefined) : Durable.ON_SETTLE, settled };
    });
  }

  /** The refusal of an update of a pending row of a sheet (see `update`): never sent. */
  #pendingUnaddressable(): RowWriteError {
    return new RowWriteError({
      step: "update",
      method: "PATCH",
      path: "/api/rows/{rowKey}",
      failure: { outcome: "unaddressable" },
      details: `row ${this.id.toString()} is pending (not yet persisted): ` +
        "update it through the SheetRow.Handle that promotes it",
    });
  }

  /** The parent a parent-stored virtual row forwards its updates to: through its sheet, else its hooks. */
  #virtualParent(): Option<RowUpdates.Parent> {
    if (!this.isVirtual || !this.parentStored || !this.#virtualInfo) return null;
    const context = this.#context();
    const parent = context != null
      ? context.resolveParent(this.#virtualInfo.parentHex)
      : this.#resolveParent?.(this.#virtualInfo.parentHex) ?? null;
    return parent == null ? null : {
      update: (change, actor) => parent.#updates.update(change, actor),
      updateDurable: (change, actor) => parent.#updateDurable(change, actor),
    };
  }

  /**
   * The draft's persist (`PendingRowPersist`: its buffered updates and the
   * promote), over this row: its ledger entry and transport, its liveness,
   * the client's disposal, the failed-write recovery and the mirror.
   */
  #pendingPersist(): PendingRowPersist {
    this.#persist ??= new PendingRowPersist({
      correction: Handle.ACTOR,
      id: () => this.#inner.id,
      raw: () => this.#inner.raw,
      ledger: () => this.#writes.ledger,
      begin: (change, actor) => this.#updates.begin(change, actor, false),
      transport: (mode, rowKey) => this.#updates.transport(mode, rowKey),
      api: () => this.#api,
      assertLive: (verb) => this.#assertLive(verb),
      isPending: () => this.isPending,
      clientRowIds: () => this.#deps?.clientRowIds?.() ?? false,
      watchDisposal: (onDisposed) =>
        nonnull(this.#deps, "a pending draft's client").lifecycle.onDisposed(() =>
          onDisposed(new ClientDisposedError("row.promote"))
        ),
      afterFailedWrite: (err, ownerVerb, verb, recover) => afterFailedWrite(err, this.#cellOwner(ownerVerb), verb, recover),
      reread: (rowKey) => this.#updates.reread(rowKey),
      mayHaveCreated: (created) => {
        this.#mirror(this.#inner.id, { mayHaveCreated: created });
      },
      promoted: (actor) => this.#invalidation.emit(new RowDataChanged(actor, this.#inner.id, "pending")),
    });
    return this.#persist;
  }
}


/**
 * A single-cardinality row's one-cell reads (`SheetRow.SingleCells`'s `get`,
 * `peek`, `cell`) over the row's cell handle `rowHandle` (minted on first
 * use), checked by `assertLive` and owned by `owner`'s `CellHandle.Owner`
 * (`null`: nothing owns them). Each delivers the column's `SheetRow.DateCell`
 * stamped with the row `id`, with no cell id (`DateCell<null>`: a
 * single-cardinality row's cell is addressed by its column).
 */
function singleCellReads(
  id: RowLocalId,
  rowHandle: () => Option<RowCellCache>,
  assertLive: (verb: string) => void,
  owner: (verb: string) => Option<CellHandle.Owner>,
): Pick<SheetRow.SingleReads, "get" | "peek" | "cell"> {
  const required = (): RowCellCache => nonnull(rowHandle(), "row.single(): the row has no cell handle");
  const peek = (column: NaiveDate.Partial, verb: "row.peek" | "row.get"): Option<SheetRow.DateCell<null>> => {
    const rh = rowHandle();
    if (rh == null) return null;
    // A column of a row that turned out multi-cardinality names no one cell:
    // said so, never read as an empty column.
    if (rh.cardinality === "multi") throw turnedMulti(id, verb);
    const entry = rh.getAllInRange(column.toRange())[0] ?? null;
    // The column's one cell is addressed by its column, never by an id.
    return entry == null ? null : { ...datedCell(id, entry), id: null };
  };
  return {
    peek: (day) => peek(columnOf(day), "row.peek"),
    get: async (day) => {
      assertLive("row.get");
      const read = owner("row.get");
      read?.assertLive();
      const column = columnOf(day);
      const cached = peek(column, "row.get");
      if (cached != null) return cached;
      const rh = required();
      try {
        await rh.fetch(column.toRange().withTime().withTz(Utc));
      } catch (err) {
        throw cellReadFailure(err);
      }
      read?.assertLive();
      return peek(column, "row.get");
    },
    cell: (day) => {
      assertLive("row.cell");
      return new CellHandle(id, columnOf(day), required(), owner("row.cell"));
    },
  };
}

/**
 * Run a durable verb's body so every refusal it raises before handing off —
 * a disposed row or sheet, a target the row cannot address — reaches the
 * caller through `settled` (`applied: on-settle`, nothing shown), never as
 * a synchronous throw: a durable verb returns at once, and the idiom
 * `void row.writeDurable(t, v, actor).settled.catch(report)` must hold. The
 * `Durable` is watched (`observedDurable`): a refusal whose `settled` the
 * caller never read goes to the client's `requestErrorHandler` bound to
 * `owner` (the owning client's lifecycle; `null` for a tree no client built).
 */
function reportThroughSettled<T>(owner: Option<ClientLifecycle>, verb: () => Durable<T>): Durable<T> {
  try {
    return observedDurable(owner, verb());
  } catch (err) {
    return observedDurable(owner, { applied: Durable.ON_SETTLE, settled: Promise.reject(err) });
  }
}
